import { KEY_CODES } from "../../../gearshiftGame/constants";

const LANE_TO_CODE = Object.fromEntries(Object.entries(KEY_CODES).map(([code, lane]) => [lane, code]));

const MISSION0_RESPONSE_LANES = [1, 2, 3, 5, 6, 7];
const MISSION3_ESCAPE_LANE = 1;
const MISSION3_PERSIST_LANES = [2, 3, 5, 6];

/**
 * Keys the frontend accepts right now, read from the shared CustomKeyboardResponse's active
 * listeners - the Game's own (lane choice, driving) and the custom jsPsych plugin's (HQ message,
 * survey), which register `choices` there too - so a key stops counting the moment its listener
 * is gone. Not tied to what's visibly on screen: e.g. Space is accepted during the approach with
 * no keycap shown.
 * @returns {Set<string>}
 */
export function getAcceptedKeys(game) {
  return new Set(game?.customKeyboardResponse?.getAcceptedKeys() ?? []);
}

function acceptedLanes(accepted, lanes) {
  return lanes.filter((lane) => accepted.has(LANE_TO_CODE[lane])).map((lane) => ({ lane, code: LANE_TO_CODE[lane] }));
}

/**
 * The agent choices GF04 accepts right now, each bound to the exact key (and lane) that applies
 * it: `{ agentAction, lane?, code }`, the shape gf_gym_agents' research controller maps its
 * semantic action through. Only keys that carry an agent action are listed - brake and
 * accelerate have none, so they are left out. `[]` when there is no agent choice.
 *
 * - HQ messages (phase 6, Missions 0 and 3): `continue` -> Space.
 * - Approach (phase 1, Missions 0 and 3): `attend` -> Space once the target is in lock-on range.
 * - Mission 0 (phase 3): `press_key_N` -> lane N during lane choice.
 * - Mission 3 (phase 3): `escape` -> lane 1 once the escape package is on screen;
 *   `persist:N` -> lane N for code responses 2, 3, 5, 6.
 */
export function getLegalActions(game, missionNumber) {
  if (!game) return [];
  const accepted = getAcceptedKeys(game);
  const phase = game.gamePhase;

  // HQ messages (phase 6) wait for Space; the agent's action there is `continue`.
  if ((missionNumber === 0 || missionNumber === 3) && phase === 6) {
    return accepted.has("Space") ? [{ agentAction: "continue", code: "Space" }] : [];
  }

  // Space is accepted the whole approach but only acts inside the proximity zone. attend is
  // the agent's only action here, so offering it earlier would just force an early, ignored
  // press - it opens once the target is in lock-on range. Missions 0 and 3 share this approach.
  if ((missionNumber === 0 || missionNumber === 3) && phase === 1) {
    return accepted.has("Space") && game.checkProximityZone?.() === true
      ? [{ agentAction: "attend", code: "Space" }]
      : [];
  }

  if (missionNumber === 0) {
    if (phase === 3) {
      return acceptedLanes(accepted, MISSION0_RESPONSE_LANES).map(({ lane, code }) => ({
        agentAction: `press_key_${lane}`,
        lane,
        code,
      }));
    }
    return [];
  }

  if (missionNumber === 3 && phase === 3) {
    const actions = [];
    // The escape lane is accepted from the start of phase 3, but the player can't see the
    // escape option until its package spawns.
    const escapeVisible = game.escapePackageOnTime != null;
    if (escapeVisible && accepted.has(LANE_TO_CODE[MISSION3_ESCAPE_LANE])) {
      actions.push({ agentAction: "escape", lane: MISSION3_ESCAPE_LANE, code: LANE_TO_CODE[MISSION3_ESCAPE_LANE] });
    }
    acceptedLanes(accepted, MISSION3_PERSIST_LANES).forEach(({ lane, code }) =>
      actions.push({ agentAction: `persist:${lane}`, lane, code })
    );
    return actions;
  }

  return [];
}
